<!-- filepath: untitled:Untitled-4 -->
<template>
  <div class="summary-section" :data-aos="isInternalView ? '' : 'fade-up'" data-aos-once="true">
    <v-container>
      <v-expansion-panels v-model="panel" dark accordion multiple>
        <v-expansion-panel expand>
          <v-expansion-panel-header>
            <v-app-bar dark color="#212121">
              <v-toolbar-title style="text-align:center;">{{ sectionTitle }}</v-toolbar-title>
              <v-spacer></v-spacer>
              
              <v-menu
                :close-on-content-click="false"
                bottom
                left
                v-if="showRequiredActions()"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openColSettingsModal"
                  >
                    <i class="fas fa-cog fa-lg" data-toggle="tooltip" data-placement="top" title="Settings"></i>
                  </v-btn>
                </template>
              </v-menu>
              <v-btn
                v-if="showRequiredActions()"
                icon
                color="primary"
                @click="downloadSummary"
              >
                <i class="fas fa-download fa-lg" data-toggle="tooltip" data-placement="top" title="Download"></i>
              </v-btn>
              <v-btn
                v-if="isInternalView && showRequiredActions()"
                icon
                color="primary"
                @click.stop="$emit('open-analysis')"
              >
                <i class="fas fa-magic fa-lg" data-toggle="tooltip" data-placement="top" title="AI Analysis"></i>
              </v-btn>
            </v-app-bar>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card :id="sectionId" class="mx-auto" outlined color="#000000">
              <v-card-text>
                <div>
                  <!-- Group Filter Buttons (right aligned, no "All" button) -->
                  <div v-if="groups.length > 0" class="group-filter-btns-right">
                    <div class="group-btns">
                      <v-btn
                        v-for="group in groups"
                        :key="group.groupID"
                        :color="selectedGroup === group.groupID ? 'orange' : '#fff'"
                        :class="['group-btn', { 'active-btn': selectedGroup === group.groupID }]"
                        @click="selectedGroup = selectedGroup === group.groupID ? null : group.groupID"
                        depressed
                        small
                        style="margin-right:8px; color: #212121;"
                      >
                        {{ group.groupName || group.groupID }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="text-right mb-2">
                    <!-- Save and Cancel buttons for drag-and-drop reordering -->
                    <v-btn
                      @click.native.stop
                      @click="cancelPostOrder"
                      color="warning"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      Cancel
                    </v-btn>

                    <v-btn
                      @click.native.stop
                      @click="savePostOrder"
                      color="success"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      <v-icon small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                  </div>
                  <v-data-table
                    :id="`${sectionId}-table`"
                    class="custom-table"
                    :headers="computedHeaders"
                    :items="filteredPosts"
                    :items-per-page="50"
                    :hide-default-footer="true"
                    v-sortable-data-table
                    @sorted="handleDataTableDragDrop"
                    :custom-sort="customSort"
                  >
                    <template v-for="(h, index) in computedHeaders" v-slot:[`header.${h.value}`]="{ header }">
                      <legend-icon :key="index" :header="h" />
                    </template>
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="move-cursor"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <v-btn
                          v-if="isInternalView"
                          class="btn-reload"
                          text
                          x-small
                          color="primary"
                          @click="refreshReportInf(item.id)"
                        >
                          <v-icon dark small>
                            mdi-reload
                          </v-icon>
                        </v-btn>
                        <a class="caption" style="color:#fff" :href="profileUrl(item)" target="_blank">@{{item.name}}</a>
                      </div>
                    </template>
                    <template v-slot:item.outreach="{ item }">
                      {{item.outreach === 0 || !item.outreach ? '-' : item.outreach | thousandSeprator}}
                    </template>
                    <template v-slot:item.url="{ item }">
                      <a v-if="item.url" :href="item.url" target="_blank" style="color:#fff">View Post</a>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:item.like_count="{ item }">
                      {{item.like_count === 0 ? '-' : item.like_count | thousandSeprator}}
                    </template>
                    <template v-slot:item.comment_count="{ item }">
                      {{item.comment_count === 0 ? '-' : item.comment_count | thousandSeprator}}
                    </template>
                    <template v-slot:item.views="{ item }">
                      {{item.views === 0 ? '-' : item.views | thousandSeprator}}
                    </template>
                    <template v-slot:item.impressions="{ item }">
                      {{item.impressions === 0 ? '-' : item.impressions | thousandSeprator}}
                    </template>
                    <template v-slot:item.reach="{ item }">
                      {{item.reach === 0 ? '-' : item.reach | thousandSeprator}}
                    </template>
                    <template v-slot:item.video_views="{ item }">
                      {{item.video_views === 0 ? '-' : item.video_views | thousandSeprator}}
                    </template>
                    <template v-slot:item.profile_viewers="{ item }">
                      {{item.profile_viewers === 0 ? '-' : item.profile_viewers | thousandSeprator}}
                    </template>
                    <template v-slot:item.shares="{ item }">
                      {{item.shares === 0 ? '-' : item.shares | thousandSeprator}}
                    </template>
                    <template v-slot:item.bookmarks="{ item }">
                      {{item.bookmarks === 0 ? '-' : item.bookmarks | thousandSeprator}}
                    </template>
                    <template slot="body.append">
                      <tr style="font-weight:500;">
                        <td><strong>TOTAL</strong></td>
                        <template v-for="header in computedHeaders">
                          <td :key="header.value" v-if="header.value !== 'name'">
                            <strong>
                              <template v-if="header.value === 'live_date'">-</template>
                              <template v-else-if="header.value === 'url'">-</template>
                              <template v-else-if="header.value === 'outreach'">{{ totalOutreach === 0 ? '-' : totalOutreach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'like_count'">{{ totalLikes === 0 ? '-' : totalLikes | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'comment_count'">{{ totalComments === 0 ? '-' : totalComments | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'views'">{{ totalViews === 0 ? '-' : totalViews | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'impressions'">{{ totalImpressions === 0 ? '-' : totalImpressions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'reach'">{{ totalReach === 0 ? '-' : totalReach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'video_views'">{{ totalVideoViews === 0 ? '-' : totalVideoViews | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'profile_viewers'">{{ totalProfileViewers === 0 ? '-' : totalProfileViewers | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'shares'">{{ totalShares === 0 ? '-' : totalShares | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'bookmarks'">{{ totalBookmarks === 0 ? '-' : totalBookmarks | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'interactions'">{{ totalInteractions === 0 ? '-' : totalInteractions | thousandSeprator }}</template>
                            </strong>
                          </td>
                        </template>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
                <!-- <legend-section
                  :showleftSideIcon="true"
                  :rightSections="storyLegends"
                ></legend-section> -->
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      :title="sectionTitle"
      :items="tableColsItems"
      @updated="updateFbAnalysisStatus"
      :isSort="true"
    />
  </div>
</template>

<style scoped>
.theme--dark.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
  border: thin solid rgba(255, 255, 255, 0.12) !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  cursor: move;
}
.move-cursor {
  cursor: move;
  float: left;
}
.group-filter-btns-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}
.group-btns {
  display: flex;
  align-items: center;
}
.group-btn {
  min-width: 48px;
  font-weight: 500;
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #212121 !important;
  transition: background 0.2s, color 0.2s;
}
.active-btn {
  background: orange !important;
  color: #fff !important;
}
</style>

<script>
/* eslint-disable */
import html2canvas from 'html2canvas';
import LegendSection from './Legend'
import LegendIcon from './LegendIcon'
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import campaignHelper from '../views/Campaign/campaignHelper';
import config from '@/config'
import { getReportProfileUrl } from '@/utils/reportPlatform'
export default {
  name: 'rpt_story_summary',
  props: ['posts', 'campaign', 'defaultCols', 'isInternalView', 'summaryType'],
  components: {
    LegendSection,
    LegendIcon,
    TableColsShowHideModal
  },
  data () {
    return {
      page: 1,
      sortBy: 'live_date',
      sortDesc: true,
      dataTableKey: 0,
      disableSaveArrange: true,
      lineUpItems: [],
      panel: [0, 1],
      storyLegends: [],
      selectedGroup: null,
      colSettingsDialog: false,
      tableColsItems: [],
      editPostOrder: false,
      originalPosts: [],
      headers: [
        { text: 'Influencer', align: 'start', sortable: false, value: 'name' },
        { text: 'Live Date', value: 'live_date', legendText: 'Live Date' },
        { text: 'Post URL', value: 'url', legendText: 'Link to the published post' },
        { text: 'Audience Size', value: 'outreach', legendText: 'The number of followers the creator has at the time where the post was made ' },
        { text: 'Likes', value: 'like_count', legendText: `The total number of likes` },
        { text: 'Comments', value: 'comment_count', legendText: 'The number of comments.' },
        { text: 'Views', value: 'views', legendText: 'The number of views.' },
        { text: 'Impressions', value: 'impressions', legendText: 'The number of times the post was shown.' },
        { text: 'Members reached', value: 'reach', legendText: 'The number of unique LinkedIn members who saw the post.' },
        { text: 'Video views', value: 'video_views', legendText: 'The number of times the LinkedIn video was viewed.' },
        { text: 'Profile viewers from this post', value: 'profile_viewers', legendText: 'The number of profile views generated by the post.' },
        { text: 'Shares', value: 'shares', legendText: 'The number of times users have reposted / shared a story.' },
        { text: 'Bookmarks', value: 'bookmarks', legendText: 'The number of bookmarks.' },
        { text: 'Interactions', value: 'interactions', legendText: 'The sum of likes, comments, saves, shares' }
      ],
      showHideFields: [
        { name: 'liveDate', label: 'Live Date', value: 'live_date' },
        { name: 'url', label: 'Post URL', value: 'url' },
        { name: 'showLikes', label: 'Likes', value: 'like_count' },
        { name: 'showComments', label: 'Comments', value: 'comment_count' },
        { name: 'showViews', label: 'Views', value: 'views' },
        { name: 'showImpressions', label: 'Impressions', value: 'impressions' },
        { name: 'showReach', label: 'Members reached', value: 'reach' },
        { name: 'showVideoViews', label: 'Video views', value: 'video_views' },
        { name: 'showProfileViewers', label: 'Profile viewers from this post', value: 'profile_viewers' },
        { name: 'showShares', label: 'Shares', value: 'shares' },
        { name: 'showBookmarks', label: 'Bookmarks', value: 'bookmarks' },
        { name: 'showInteractions', label: 'Interactions', value: 'interactions' }
      ],
      storyRightSection: [
        { title: 'Live Date', description: 'Live Date' },
        { title: 'Audience Size', description: 'Audience size (followers at post time)' },
        { title: 'Shares', description: 'Number of times story was shared' },
        { title: 'Replies', description: 'Number of replies to photo or video in your stories' },
        { title: 'Links Click', description: 'Number of people who swipe up on a story to follow a link' },
        { title: 'Sticker Taps', description: 'Number of times users interacted with the stickers/handle' },
        { title: 'Reach', description: 'Number of unique user that seen your content' },
        { title: 'Impressions', description: 'Total number of times content have been shown to users' },
        { title: 'Average Views', description: 'Average views over the last 30 posts' },
        { title: 'Average Reels Plays', description: 'Average plays over the last 30 posts' }
      ]
    }
  },
  computed: {
    sortedPosts() {
      return this.posts
    },
    currentUserRole: function() {
      return Number(this.$store.state.user.role);
    },
    isLinkedIn() {
      return this.summaryType === 'linkedin'
    },
    isTwitter() {
      return this.summaryType === 'twitter'
    },
    sectionTitle() {
      return this.isLinkedIn ? 'LinkedIn Analysis Summary' : (this.isTwitter ? 'X (Twitter) Analysis Summary' : 'Facebook Analysis Summary')
    },
    sectionId() {
      return this.isLinkedIn ? 'linkedin-analysis-summary' : (this.isTwitter ? 'twitter-analysis-summary' : 'fb-analysis-summary')
    },
    analysisStatus() {
      if (this.isLinkedIn) return this.campaign.linkedInAnalysisStatus || this.defaultCols || {}
      if (this.isTwitter) return this.campaign.twitterAnalysisStatus || this.defaultCols || {}
      return this.campaign.fbAnalysisStatus || this.defaultCols || {}
    },
    availableShowHideFields() {
      if (this.isLinkedIn) {
        const labels = {
          showLikes: 'Reactions',
          showShares: 'Reposts',
          showBookmarks: 'Saves'
        }
        return this.showHideFields
          .filter(field => ['liveDate', 'url', 'showImpressions', 'showReach', 'showVideoViews', 'showProfileViewers', 'showLikes', 'showComments', 'showShares', 'showBookmarks'].includes(field.name))
          .map(field => Object.assign({}, field, { label: labels[field.name] || field.label }))
      }
      if (this.isTwitter) {
        const labels = {
          showComments: 'Replies',
          showShares: 'Reposts'
        }
        return this.showHideFields
          .filter(field => ['liveDate', 'url', 'showImpressions', 'showComments', 'showLikes', 'showShares', 'showBookmarks'].includes(field.name))
          .map(field => Object.assign({}, field, { label: labels[field.name] || field.label }))
      }
      return this.showHideFields
    },
    groups() {
      const groupMap = new Map();
      this.sortedPosts.forEach(post => {
        if (post.groupID && post.groupName) {
          groupMap.set(post.groupID, post.groupName);
        }
        if (Array.isArray(post.reports)) {
          post.reports.forEach(report => {
            if (report.groupID && report.groupName) {
              groupMap.set(report.groupID, report.groupName);
            }
          });
        }
      });
      return Array.from(groupMap, ([groupID, groupName]) => ({
        groupID,
        groupName
      }));
    },
    filteredPosts() {
      if (!this.selectedGroup) return this.sortedPosts;
      return this.sortedPosts.filter(post => {
        if (post.groupID === this.selectedGroup) return true;
        if (Array.isArray(post.reports)) {
          return post.reports.some(report => report.groupID === this.selectedGroup);
        }
        return false;
      });
    },
    computedHeaders() {
      const status = this.analysisStatus;
      const finalHeaders = [];

      // 1. Add the "Influencer" column first (always fixed)
      const nameHeader = this.headers.find(h => h.value === 'name');
      if (nameHeader) {
        finalHeaders.push(nameHeader);
      }

      // 2. Iterate through the keys of fbAnalysisStatus to determine order and visibility
      for (const statusKey in status) {
        if (Object.prototype.hasOwnProperty.call(status, statusKey)) {
          const isVisible = status[statusKey];
          if (isVisible) {
            // Find the corresponding header from `headers` using `showHideFields` as a map
            const field = this.availableShowHideFields.find(f => f.name === statusKey);
            if (field) {
              const header = this.headers.find(h => h.value === field.value);
              if (header) {
                finalHeaders.push(header);
              }
            } else {
              // Fallback for keys that might directly match header values
              const header = this.headers.find(h => h.value === statusKey);
              if (header) {
                finalHeaders.push(header);
              }
            }
          }
        }
      }
      if (this.isTwitter) {
        return finalHeaders.map(header => {
          if (header.value === 'comment_count') return Object.assign({}, header, { text: 'Replies' })
          if (header.value === 'shares') return Object.assign({}, header, { text: 'Reposts' })
          return header
        })
      }
      if (this.isLinkedIn) {
        return finalHeaders.map(header => {
          if (header.value === 'like_count') return Object.assign({}, header, { text: 'Reactions' })
          if (header.value === 'shares') return Object.assign({}, header, { text: 'Reposts' })
          if (header.value === 'bookmarks') return Object.assign({}, header, { text: 'Saves' })
          return header
        })
      }
      return finalHeaders
    },
    totalOutreach() {
      let outreach = 0;
      this.filteredPosts.map(item => {
        outreach += item.outreach ? Number(item.outreach) : 0
      })
      return outreach
    },
    totalLikes() {
      let likeCount = 0;
      this.filteredPosts.map(item => {
        likeCount += item.like_count ? Number(item.like_count) : 0
      })
      return likeCount
    },
    totalComments() {
      let commentCount = 0;
      this.filteredPosts.map(item => {
        commentCount += item.comment_count ? Number(item.comment_count) : 0
      })
      return commentCount
    },
    totalViews() {
      let views = 0;
      this.filteredPosts.map(item => {
        views += item.views ? Number(item.views) : 0
      })
      return views
    },
    totalImpressions() {
      return this.filteredPosts.reduce((total, item) => total + Number(item.impressions || 0), 0)
    },
    totalReach() {
      return this.filteredPosts.reduce((total, item) => total + Number(item.reach || 0), 0)
    },
    totalVideoViews() {
      return this.filteredPosts.reduce((total, item) => total + Number(item.video_views || 0), 0)
    },
    totalProfileViewers() {
      return this.filteredPosts.reduce((total, item) => total + Number(item.profile_viewers || 0), 0)
    },
    totalShares() {
      let shares = 0;
      this.filteredPosts.map(item => {
        shares += item.shares ? Number(item.shares) : 0
      })
      return shares
    },
    totalBookmarks() {
      let bookmarks = 0;
      this.filteredPosts.map(item => {
        bookmarks += item.bookmarks ? Number(item.bookmarks) : 0
      })
      return bookmarks
    },
    totalInteractions() {
      let interactions = 0;
      this.filteredPosts.map(item => {
        interactions += item.interactions ? Number(item.interactions) : 0
      })
      return interactions
    }
  },
  mounted() {
    this.updateLegend();
  },
  methods: {
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
        if (!(isNaN(a[index[0]]))) {
          if (!isDesc[0]) {
            return (a[index[0]] - b[index[0]]);
          } else {
            return (b[index[0]] - a[index[0]]);
          }
        } else {
          if (!isDesc[0]) {
            return (a[index[0]] < b[index[0]]) ? -1 : 1;
          } else {
            return (b[index[0]] < a[index[0]]) ? -1 : 1;
          }
        }
      })
      this.lineUpItems = Object.assign([], items)
      this.disableSaveArrange = false
      return items;
    },
    updateLegend() {
      let filteredLegend = this.storyRightSection.filter(s => {
        let fbAnalysisStatus = this.analysisStatus
        if (!fbAnalysisStatus.showShares && s.title === 'Shares') return false
        if (!fbAnalysisStatus.showReplies && s.title === 'Replies') return false
        if (!fbAnalysisStatus.showLinkClicks && s.title === 'Links Click') return false
        if (!fbAnalysisStatus.showStickerTaps && s.title === 'Sticker Taps') return false
        if (!fbAnalysisStatus.showReach && s.title === 'Reach') return false
        if (!fbAnalysisStatus.showImpressions && s.title === 'Impressions') return false
        if (!fbAnalysisStatus.liveDate && s.title === 'Live Date') return false
        if (!fbAnalysisStatus.showAvgViews && s.title === 'Average Views') return false
        if (!fbAnalysisStatus.showAvgReelsPlays && s.title === 'Average Reels Plays') return false
        return true
      });
      this.storyLegends = filteredLegend
    },
    handleDataTableDragDrop(event, items) {
      // check and save drag n drop update for table items/ grouped items
      if (items.length === 0 || !this.showRequiredActions()) {
        return;
      }
      
      // Store original posts if this is the first edit
      if (!this.editPostOrder) {
        this.originalPosts = JSON.parse(JSON.stringify(this.posts));
      }
      
      const itemsPerPage = 15;
      const oldIndex = ((this.page - 1) * itemsPerPage) + event.oldIndex;
      const newIndex = ((this.page - 1) * itemsPerPage) + event.newIndex;
      let tmpPosts = []
      let groupID = items[0].groupID
      if (groupID) {
        let group = this.sortedPosts.find(p => p.id === groupID)
        if (group) {
          tmpPosts = group.reports
        }
      } else {
        tmpPosts = Object.assign([], this.sortedPosts)
      }
      const movedItem = tmpPosts.splice(oldIndex, 1)[0]
      tmpPosts.splice(newIndex, 0, movedItem)
      if (groupID) {
        this.posts = this.sortedPosts.map(p => {
          if (p.id === groupID) {
            p.reports = tmpPosts.map(tmp => tmp)
          }
          return p
        })
      } else {
        this.posts = tmpPosts.map(tmp => tmp)
      }
      this.lineUpItems = []
      this.lineUpItems = Object.assign([], tmpPosts)
      this.disableSaveArrange = false
      this.editPostOrder = true
    },
    savePostOrder() {
      this.updateLineUp();
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    cancelPostOrder() {
      // Revert to original posts order
      this.posts = JSON.parse(JSON.stringify(this.originalPosts));
      this.lineUpItems = [];
      this.disableSaveArrange = true;
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    async updateLineUp() {
      await this.lineUpItems.map((item, index) => {
        let data = {}
        if (item.reportGroup) {
          data.groupID = item.id
          data.lineUpIndex = index
          data.posts = item.reports.map((element, rindex) => {
            return Object.assign({}, { id: element.id, lineUpIndex: rindex })
          })
        } else {
          data = {
            reportID: item.id,
            campaignID: item.campaignID,
            lineUpIndex: index
          }
        }
        campaignAPIs.updateLineUpIndex(data)
      })
    },
    async updateFbAnalysisStatus(items) {
      const statusField = this.isLinkedIn ? 'linkedInAnalysisStatus' : (this.isTwitter ? 'twitterAnalysisStatus' : 'fbAnalysisStatus')
      await this.updateColumnsStatus(items, statusField, 'colSettingsDialog');
    },
    showRequiredActions: function() {
      if (!this.$store.state.token || this.$store.state.token === 'null' || !this.$store.state.user) {
        return false
      } else {
        return config.userRoles.CLIENT !== this.currentUserRole
      }
    },
    openColSettingsModal() {
      this.initializeFbAnalysisTableCols();
      this.colSettingsDialog = true
    },
    initializeFbAnalysisTableCols() {
        this.tableColsItems = campaignHelper.getOrderedColumns(
          this.availableShowHideFields,
          this.analysisStatus,
          this.defaultCols
        );
    },
    async updateColumnsStatus(items, campaignDataProp, dialogProp) {
        let newStatusObject = {};
        items.forEach((item) => {
            newStatusObject[item.key] = item.value;
        });
        let data = [
            Object.assign({}, {
                id: this.campaign.id,
                [campaignDataProp]: newStatusObject
            })
        ];
        try {
            await this.$store.dispatch('campaign/updateCampaignByIds', data);
            this.campaign[campaignDataProp] = newStatusObject;
            this[dialogProp] = false;
        } catch (error) {
            console.error('Error updating campaign settings:', error);
        }
    },
    downloadSummary() {
      const element = document.getElementById(this.sectionId);
      html2canvas(element).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${this.sectionId}.png`;
        link.click();
      });
    },
    refreshReportInf(id) {
      this.$emit('refreshReportInf', id)
    },
    profileUrl(item) {
      const fallbackPlatform = this.isLinkedIn ? 'linkedin' : (this.isTwitter ? 'twitter' : 'facebook')
      return getReportProfileUrl(item, fallbackPlatform)
    }
  }
}
</script>



// WEBPACK FOOTER //
// FbSummary.vue?3ebc5284